// Tier-availability logic for the add/edit-collector flow (ADR-050).
//
// The `/prices` endpoint returns every vendor with all placement variant rows; each row
// carries `priceUnder200` / `priceFrom200`, either of which may be null. The UI shows the
// under200 ⇄ 200+ toggle ONLY for the tiers whose price is non-null for the selected
// product:
//   - both prices present  → toggle with both bands
//   - only under200        → no toggle, base tier only
//   - only from200         → no toggle, "vast tarief" (single-rate vendor row)
//   - neither              → no priced row at all → manual €/m² required
//
// Pure functions here so they can be unit-tested without React.

import type { TierBand } from '@/api/onderaannemers';
import type { VendorPriceRow } from '@/api/onderaannemerPrijzen';

export interface TierAvailability {
  hasUnder200: boolean;
  hasFrom200: boolean;
  // Both tiers priced → the user can pick between them.
  showToggle: boolean;
  // Vendor row has only the 200+ tier (no base) → "vast tarief", no band choice.
  singleRate: boolean;
  // No priced tier at all for this product → the caller must require a typed €/m².
  hasAnyPrice: boolean;
  // The band to default to (from200 when it's the only one, else under200).
  defaultTier: TierBand;
}

export function tierAvailability(row: VendorPriceRow | null | undefined): TierAvailability {
  const hasUnder200 = row?.priceUnder200 != null;
  const hasFrom200 = row?.priceFrom200 != null;
  const hasAnyPrice = hasUnder200 || hasFrom200;
  return {
    hasUnder200,
    hasFrom200,
    showToggle: hasUnder200 && hasFrom200,
    singleRate: hasFrom200 && !hasUnder200,
    hasAnyPrice,
    defaultTier: hasUnder200 ? 'under200' : 'from200',
  };
}

/** The price for a given band on a price row, or null when that tier isn't priced. */
export function priceForTier(row: VendorPriceRow | null | undefined, tier: TierBand): number | null {
  if (!row) return null;
  return tier === 'from200' ? row.priceFrom200 : row.priceUnder200;
}
